import { TableMemory } from '@festo-ax/smartenance-shared-ui';

export const TABLE_MEMORY_PREFIX = 'smrtnc.table-memory:';

/**
 * Storage envelope for one table's view-state.
 *
 * `savedAt` is written but never read. It is here so a TTL or staleness rule can
 * be added later without migrating entries written before it. Not named
 * `invalidAt` — `StorageService` treats that key as its own TTL envelope.
 */
type StoredTableMemory = {
  savedAt: string;
  view: TableMemory;
};

/** A `Date` on its way through JSON. Tagged, because a filter value may legitimately be a date-shaped string. */
type TaggedDate = {
  __date: string;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object'
    && value !== null
    && !Array.isArray(value)
    && !(value instanceof Date);
}

function isTaggedDate(value: unknown): value is TaggedDate {
  return isPlainObject(value)
    && Object.keys(value).length === 1
    && typeof value['__date'] === 'string';
}

function encode(value: unknown): unknown {
  if (value instanceof Date) {
    return { __date: value.toISOString() };
  }

  if (Array.isArray(value)) {
    return value.map(encode);
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, encode(item)]));
  }

  return value;
}

function decode(value: unknown): unknown {
  if (isTaggedDate(value)) {
    return new Date(value.__date);
  }

  if (Array.isArray(value)) {
    return value.map(decode);
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, decode(item)]));
  }

  return value;
}

export function storageKey(userId: string, tableName: string): string {
  return `${TABLE_MEMORY_PREFIX}${userId}:${tableName}`;
}

export function isTableMemoryKey(key: string): boolean {
  return key.startsWith(TABLE_MEMORY_PREFIX);
}

/** Prefix match, never `split(':')` — a table name may itself contain a colon (`machine-incidents:42`). */
export function isKeyOfUser(key: string, userId: string): boolean {
  return key.startsWith(`${TABLE_MEMORY_PREFIX}${userId}:`);
}

export function toStorable(view: TableMemory): StoredTableMemory {
  return {
    savedAt: new Date().toISOString(),
    view: encode(view) as TableMemory,
  };
}

/** Returns `undefined` for anything that is not a recognisable envelope — a corrupt entry reads as "no snapshot". */
export function fromStorable(raw: unknown): TableMemory | undefined {
  if (!isPlainObject(raw) || typeof raw['savedAt'] !== 'string' || !isPlainObject(raw['view'])) {
    return undefined;
  }

  return decode(raw['view']) as TableMemory;
}
